import type { RxJsonSchema } from 'rxdb'

/**
 * Reaction Document Type (Denormalized for RxDB)
 */
export interface ReactionDocument {
  id: string
  envId: string
  entityType: string
  entityId: string
  emoji: string
  /** Human-readable emoji label, e.g. "thumbs up" */
  emojiLabel: string | null
  /** Emoji shortcode, e.g. ":thumbs_up:" */
  emojiShortcode: string | null
  userId: string
  isDeleted: boolean

  // Denormalized user info
  userName: string | null
  userEmail: string | null

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn: number | null
  deletedBy: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Reaction
 */
export const reactionSchema: RxJsonSchema<ReactionDocument> = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    entityType: {
      type: 'string',
      maxLength: 50,
    },
    entityId: {
      type: 'string',
      maxLength: 36,
    },
    emoji: {
      type: 'string',
      maxLength: 10,
    },
    emojiLabel: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    emojiShortcode: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    userId: {
      type: 'string',
      maxLength: 36,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Denormalized fields
    userName: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    userEmail: {
      type: ['string', 'null'],
      maxLength: 255,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'entityType',
    'entityId',
    'emoji',
    'userId',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'entityId',
    'userId',
    'updatedOn',
    ['entityId', 'isDeleted'],
  ],
}

export const reactionMigrationStrategies = {
  // v0 -> v1: backfill `emojiLabel`, `emojiShortcode` (added, nullable).
  1: (oldDoc: any): any => ({
    ...oldDoc,
    emojiLabel: oldDoc.emojiLabel ?? null,
    emojiShortcode: oldDoc.emojiShortcode ?? null,
  }),
}
